import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';

import { AxiosResponse } from 'axios';
import { useOnClickOutside } from 'usehooks-ts';

import { SingleSelect } from '@/common';
import { UploadButton } from '@/components/common';
import { Search } from '@/components/common/Search';
import { Spinner } from '@/components/generic';
import { queryClient } from '@/configuration/react-query';
import { STOCK_CLIP } from '@/constants';
import { UPLOAD_PICKER_TYPES } from '@/constants/UploadFileConstants';
import { AssetUploadType, COLORS } from '@/enums';
import { getAssetFilterOptions, getContentUploadOptions } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload, useUploadFilePicker } from '@/hooks';
import { AssetsTagFilter } from '@/pages/Event/Asset/AssetTagFilter';
import { OrganizationAssetsModal } from '@/pages/Event/Asset/OrganizationAssetsModal';
import { PhotoFilmIcon } from '@/resources';
import { useCreateAssetCloneMutation } from '@/services';
import {
  Asset,
  AssetClonePayload,
  AssetContainerHeaderProps,
  FilePickerType,
  SelectOptionProps
} from '@/types';

export const AssetContainerHeader = ({
  eventId,
  organizationId,
  searchCallback,
  setFilterByType,
  filterByType,
  setSelectedTags,
  selectedTags,
  isPublic = false,
  assetType,
  organizationPermissions
}: AssetContainerHeaderProps) => {
  const [t] = useTranslation('common');
  const selectRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOpenOrganizationAssetsModal, setIsOpenOrganizationAssetsModal] = useState(false);
  const [isCloningOrganizationAssets, setIsCloningOrganizationAssets] = useState(false);

  const pickerTypes = useMemo<FilePickerType[]>(() => {
    return assetType === STOCK_CLIP ? UPLOAD_PICKER_TYPES.VIDEO : UPLOAD_PICKER_TYPES.MEDIA;
  }, [assetType]);

  const { uploadFiles, isUploading } = useFileUpload({
    eventId,
    isPublic,
    pickerTypes,
    uploadAssetType: assetType,
    organizationId
  });

  const isProcessingAssets = isUploading || isCloningOrganizationAssets;

  const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes,
    multiple: true,
    disabled: isProcessingAssets,
    onFilesSelected: uploadFiles
  });

  const { mutateAsync: createAssetClone } = useCreateAssetCloneMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.assetName })
      );
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  useOnClickOutside([selectRef], () => setIsMenuOpen(false));

  const handleUploadSourceChange = async (selectedOption: SingleValue<SelectOptionProps>) => {
    setIsMenuOpen(false);

    if (selectedOption?.value === AssetUploadType.UPLOAD_NEW) {
      await openFilePicker();
      return;
    }

    setIsOpenOrganizationAssetsModal(true);
  };

  const handleSelectAsset = async (asset: Asset) => {
    const payload: AssetClonePayload = {
      assetId: asset.id,
      eventId: eventId ? Number(eventId) : undefined,
      organizationId: organizationId ?? undefined
    };

    await createAssetClone(payload);
  };

  return (
    <div className='mb-5 flex w-full flex-col justify-between md:h-12 md:flex-row'>
      <div className='mb-4 flex w-full flex-col items-center justify-between gap-4 md:mb-0 md:w-1/2 md:flex-row'>
        <Search callback={searchCallback} />

        {!isPublic && (
          <div className='flex w-full flex-row justify-between gap-2'>
            <SingleSelect
              containerClassName='w-1/2 md:w-36'
              className='w-full md:w-36'
              label=''
              isClearable
              id='assetFilter'
              placeholder={t('filters')}
              options={getAssetFilterOptions(organizationPermissions)}
              handleChange={(value) => setFilterByType(value as SelectOptionProps)}
              value={filterByType}
              textColor={COLORS.PRIMARY_BLUE}
              borderStyle={`1px solid ${COLORS.PRIMARY_BLUE}`}
              fontWeight='!font-bold'
              dropdownIndicatorColor={COLORS.PRIMARY_BLUE}
            />

            {eventId && (
              <AssetsTagFilter
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
                eventId={eventId}
              />
            )}
          </div>
        )}
      </div>

      {isPublic && (
        <UploadButton
          multiple={true}
          text='uploadNewContent'
          pickerTypes={pickerTypes}
          isUploading={isProcessingAssets}
          showMaxFileSize
          icon={<PhotoFilmIcon className='mr-1 size-4 text-primary-blue' />}
          onFilesSelected={uploadFiles}
        />
      )}

      {!isPublic && (
        <>
          <input {...hiddenInputProps} />

          <div ref={selectRef} className='min-w-56 py-1'>
            <SingleSelect
              id='upload-new-content'
              value={null}
              placeholder={t('addMedia')}
              customPlaceholder={
                isProcessingAssets ? (
                  <span className='flex flex-row items-center gap-x-2 font-bold text-secondary-steel-blue'>
                    <Spinner className='mr-2 size-4' />
                    <div className='text-sm'>{t('uploading')}</div>
                  </span>
                ) : undefined
              }
              options={getContentUploadOptions() as unknown as SelectOptionProps[]}
              containerClassName=''
              handleChange={handleUploadSourceChange}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuClose={() => setIsMenuOpen(false)}
              isLiveStream
              menuIsOpen={isMenuOpen}
              placeholderStyles={{ marginTop: '-20px' }}
              disabled={isProcessingAssets}
            />
          </div>
        </>
      )}

      {isOpenOrganizationAssetsModal && (
        <OrganizationAssetsModal
          handleSelectAsset={handleSelectAsset}
          isOpenOrganizationAssetsModal={isOpenOrganizationAssetsModal}
          setIsOpenOrganizationAssetsModal={setIsOpenOrganizationAssetsModal}
          setIsUploading={setIsCloningOrganizationAssets}
          organizationId={organizationId ?? null}
        />
      )}
    </div>
  );
};
